import React, { useMemo } from 'react';
import { Card, Alert, Button, Spinner } from 'react-bootstrap';
import { FiMail } from 'react-icons/fi';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useEndOfStayDashboard } from './hooks/useDashboardData';
import DashboardPageHeader from './components/DashboardPageHeader';
import OperationsNav from './components/OperationsNav';
import EndOfStayFilters from './components/EndOfStayFilters';
import EndOfStaySection from './components/EndOfStaySection';
import { END_OF_STAY_SECTION_KEYS, END_OF_STAY_SCOPE_OPTIONS } from './utils/dashboardConstants';
import 'bootstrap/dist/css/bootstrap.css';
import './operations-dashboard.css';

const EndOfStayDashboard = () => {
    const { t } = useTranslation();
    const { filters, setFilter, resetFilters, hasActiveFilters } = useDashboardFilters();

    const eosFilters = useMemo(() => {
        const validScope = END_OF_STAY_SCOPE_OPTIONS.some((o) => o.value === filters.scope);
        return validScope ? filters : { ...filters, scope: 'all' };
    }, [filters]);

    const { data, loading, error, refetch, isError } = useEndOfStayDashboard(eosFilters);

    const activeSections = useMemo(() => {
        if (!data) return [];

        if (eosFilters.scope !== 'all') {
            const section = data[eosFilters.scope];
            return section ? [{ key: eosFilters.scope, section }] : [];
        }

        return END_OF_STAY_SECTION_KEYS
            .map((key) => ({ key, section: data[key] }))
            .filter(({ section }) => section !== null && section !== undefined);
    }, [data, eosFilters.scope]);

    const totalPolicies = activeSections.reduce(
        (sum, { section }) => sum + (section?.total ?? section?.items?.length ?? 0),
        0
    );

    if (isError && !data) {
        return (
            <div className="dashboard-page">
                <Alert variant="danger">
                    <Alert.Heading>Erreur de chargement</Alert.Heading>
                    <p className="mb-3">{error}</p>
                    <Button variant="outline-danger" size="sm" onClick={refetch}>
                        Réessayer
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <Card className="admin-ds-main-card">
                <Card.Body>
                    <DashboardPageHeader
                        title={t('pages.operationsDashboard.endOfStayTitle')}
                        icon={FiMail}
                        referenceDate={data?.reference_date}
                        metadata={data?.metadata}
                        onRefresh={refetch}
                        refreshing={loading}
                    />

                    <OperationsNav />

                    <EndOfStayFilters
                        filters={filters}
                        onFilterChange={setFilter}
                        onReset={resetFilters}
                        hasActiveFilters={hasActiveFilters}
                    />

                    {data?.timezone && (
                        <p className="text-muted small mb-3">
                            Fuseau : {data.timezone}
                            {totalPolicies === 0 && !loading && (
                                <> · Aucune police éligible pour les filtres sélectionnés</>
                            )}
                        </p>
                    )}

                    {activeSections.length === 0 && !loading && data && (
                        <Alert variant="info" className="mb-0">
                            Aucune police fin de séjour pour cette plage.
                        </Alert>
                    )}

                    {activeSections.map(({ key, section }) => (
                        <EndOfStaySection
                            key={key}
                            sectionKey={key}
                            section={section}
                            loading={loading && !data}
                        />
                    ))}

                    {loading && data && (
                        <div className="text-muted small text-end mt-3">
                            <Spinner animation="border" size="sm" className="me-1" />
                            Mise à jour…
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default EndOfStayDashboard;
